import { useState, useEffect, useRef, useCallback } from 'react';
import { HyperFormula } from 'hyperformula';
import { devLog } from '../../utils/devLogger';

interface InputInfo {
  element: HTMLInputElement;
  memberName: string;
  qname: string;
  contextRef: string;
  cellKey: string;
  cellAddress: any;
  isParentMember: boolean;
  verticalType: string | null; // 'cost-opening', 'cost-closing', 'depr-opening', 'depr-closing', 'nbv-opening', 'nbv-closing'
}

interface RowData {
  inputs: InputInfo[];
  inputsByMember: Map<string, InputInfo>;
  parentInput: InputInfo | null;
  verticalFields: InputInfo[];
  hasVerticalCalculation: boolean;
}

interface UseHyperFormulaPPEReturn {
  engine: HyperFormula | null;
  isReady: boolean;
  readOnlyFields: Set<string>;
  handleFieldChange: (fieldName: string, contextRef: string, value: string) => void;
  getCalculatedValue: (fieldName: string, contextRef: string) => number | null;
  refreshAllCalculations: () => void;
}

/**
 * HyperFormula hook for Singapore PPE (Property, Plant and Equipment) and IA (Intangible Assets) tables
 * Following the bulletproof method from useHyperFormulaSOCIE.ts
 * 
 * Rules:
 * 1. data-cost-closing = sum of 5 rows above (same column, same table)
 * 2. data-depr-closing = sum of 7 rows above (same column, same table)
 * 3. data-nbv-opening = data-cost-opening - data-depr-opening (same column, same table)
 * 4. data-nbv-closing = data-cost-closing - data-depr-closing (same column, same table)
 * 5. Each row sums to parent member column (PropertyPlantAndEquipmentMember or IntangibleAssetsMember)
 */
export const useHyperFormulaPPESG = (): UseHyperFormulaPPEReturn => {
  const [engine, setEngine] = useState<HyperFormula | null>(null);
  const [isReady, setIsReady] = useState(false);

  // Refs to store calculation data (like useHyperFormulaSOCIE)
  const sheetRef = useRef<number>(0);
  const cellMapRef = useRef<Record<string, any>>({});
  const inputsByRowRef = useRef<Map<number, RowData>>(new Map());
  const rowIndicesRef = useRef<Map<HTMLTableRowElement, number>>(new Map());
  const updatingCellRef = useRef<boolean>(false);
  const readOnlyFieldsRef = useRef<Set<string>>(new Set());

  // Format number exactly like original script
  const formatNumber = useCallback((value: any): string => {
    if (typeof value !== 'number' || isNaN(value)) {
      value = 0;
    }

    if (typeof window !== 'undefined' && (window as any).numeral) {
      return (window as any).numeral(value).format('0,0.00');
    }

    return value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  }, []);

  // Map inputs by row (following SOCIE pattern)
  const mapInputsByRow = useCallback((hfEngine: HyperFormula) => {
    let cellCount = 0;

    // Index all rows first
    const rows = document.querySelectorAll('.socie-table tr');
    rows.forEach((row, index) => {
      rowIndicesRef.current.set(row as HTMLTableRowElement, index);
    });

    rows.forEach(row => {
      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData: RowData = {
        inputs: [],
        inputsByMember: new Map(),
        parentInput: null,
        verticalFields: [],
        hasVerticalCalculation: false
      };

      // Check if this row has vertical calculation fields
      const hasVerticalCalc = row.querySelector(
        'input[data-cost-closing], input[data-depr-closing], input[data-nbv-opening], input[data-nbv-closing]'
      );

      if (hasVerticalCalc) {
        rowData.hasVerticalCalculation = true;
      }

      row.querySelectorAll('input[data-equity-member][data-qname]').forEach(inputEl => {
        const input = inputEl as HTMLInputElement;
        const memberName = input.dataset.equityMember;
        const contextRef = input.dataset.contextRef;
        const qname = input.dataset.qname;

        if (!memberName || !contextRef || !qname) return;

        // Determine vertical type from data attributes
        let verticalType: string | null = null;
        if (input.dataset.costOpening === 'true') {
          verticalType = 'cost-opening';
        } else if (input.dataset.costClosing === 'true') {
          verticalType = 'cost-closing';
        } else if (input.dataset.deprOpening === 'true') {
          verticalType = 'depr-opening';
        } else if (input.dataset.deprClosing === 'true') {
          verticalType = 'depr-closing';
        } else if (input.dataset.nbvOpening === 'true') {
          verticalType = 'nbv-opening';
        } else if (input.dataset.nbvClosing === 'true') {
          verticalType = 'nbv-closing';
        }

        const cellKey = `r${rowIndex}_${memberName}_${qname}_${contextRef}`;
        const cellAddress = {
          sheet: sheetRef.current,
          col: 0,
          row: cellCount++
        };

        cellMapRef.current[cellKey] = cellAddress;

        const isParentMember = memberName === 'PropertyPlantAndEquipmentMember' || memberName === 'IntangibleAssetsMember';

        const inputInfo: InputInfo = {
          element: input,
          memberName,
          qname,
          contextRef,
          cellKey,
          cellAddress,
          isParentMember,
          verticalType
        };

        rowData.inputs.push(inputInfo);
        rowData.inputsByMember.set(memberName, inputInfo);

        if (isParentMember) {
          rowData.parentInput = inputInfo;
        }

        // Determine if readonly
        let isReadonly = false;

        // Parent member is readonly (horizontal sum target)
        if (isParentMember) {
          isReadonly = true;
        }

        // Vertical calculation targets are readonly
        if (verticalType === 'cost-closing' || verticalType === 'depr-closing' ||
            verticalType === 'nbv-opening' || verticalType === 'nbv-closing') {
          isReadonly = true;
          rowData.verticalFields.push(inputInfo);
        }

        if (isReadonly) {
          input.readOnly = true;
          if (input.name && contextRef) {
            const uniqueKey = `${input.name}_${contextRef}`;
            readOnlyFieldsRef.current.add(uniqueKey);
          }
        }

        // Set initial value in HyperFormula
        let value = 0;
        if (input.value && input.value.trim() !== '') {
          if ((window as any).numeral) {
            value = (window as any).numeral(input.value).value() || 0;
          } else {
            value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
          }
        }

        hfEngine.setCellContents(cellAddress, value);
      });

      if (rowData.inputs.length > 0) {
        inputsByRowRef.current.set(rowIndex, rowData);
      }
    });

    devLog(`[PPE HF] Mapped ${cellCount} inputs across ${inputsByRowRef.current.size} rows`);
  }, []);

  // Setup horizontal formulas (sum to parent member)
  const setupHorizontalFormulas = useCallback((hfEngine: HyperFormula) => {
    inputsByRowRef.current.forEach((rowData, rowIndex) => {
      // Skip rows with vertical calculations for horizontal sum
      // (They still need their parent calculated, so don't skip entirely)
      
      const parentInfo = rowData.parentInput;
      if (!parentInfo) return;

      // Collect all child member inputs (non-parent)
      const childInputs = rowData.inputs.filter(info => !info.isParentMember);

      if (childInputs.length === 0) return;

      const formulaParts: string[] = [];

      childInputs.forEach(childInfo => {
        const cellRef = hfEngine.simpleCellAddressToString(childInfo.cellAddress, sheetRef.current);
        if (cellRef) {
          formulaParts.push(cellRef);
        }
      });

      if (formulaParts.length > 0) {
        const formula = '=' + formulaParts.join('+');
        hfEngine.setCellContents(parentInfo.cellAddress, formula);
      }
    });
  }, []);

  // Setup vertical formulas
  const setupVerticalFormulas = useCallback((hfEngine: HyperFormula) => {
    const sortedRows = Array.from(inputsByRowRef.current.entries())
      .sort(([a], [b]) => a - b);

    // Rule 1: Cost Closing = sum of 5 rows above
    setupCostClosingFormulas(hfEngine, sortedRows);

    // Rule 2: Depr Closing = sum of 7 rows above
    setupDeprClosingFormulas(hfEngine, sortedRows);

    // Rule 3: NBV Opening = Cost Opening - Depr Opening
    setupNbvOpeningFormulas(hfEngine, sortedRows);

    // Rule 4: NBV Closing = Cost Closing - Depr Closing
    setupNbvClosingFormulas(hfEngine, sortedRows);
  }, []);

  // Cost Closing = sum of 5 rows above (same column, same table)
  const setupCostClosingFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const costClosingInputs = rowData.verticalFields.filter(info => info.verticalType === 'cost-closing');

      if (costClosingInputs.length === 0) return;

      costClosingInputs.forEach(closingInfo => {
        const input = closingInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        if (currentRowIndex < 5) return;

        const formulaParts: string[] = [];

        // Sum 5 rows above (same column)
        for (let i = 1; i <= 5; i++) {
          const prevRow = rows[currentRowIndex - i] as HTMLTableRowElement;
          if (!prevRow.cells || prevRow.cells.length <= column) continue;

          const prevCell = prevRow.cells[column];
          const prevInput = prevCell?.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;

          if (!prevInput) continue;

          const prevRowIndex = rowIndicesRef.current.get(prevRow);
          if (prevRowIndex === undefined) continue;

          const prevRowData = inputsByRowRef.current.get(prevRowIndex);
          if (!prevRowData) continue;

          const prevInputInfo = prevRowData.inputs.find(info => info.element === prevInput);
          if (!prevInputInfo) continue;

          const cellRef = hfEngine.simpleCellAddressToString(prevInputInfo.cellAddress, sheetRef.current);
          if (cellRef) {
            formulaParts.push(cellRef);
          }
        }

        if (formulaParts.length > 0) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(closingInfo.cellAddress, formula);
          devLog(`[PPE HF] Cost closing ${closingInfo.memberName}: sum of ${formulaParts.length} rows above`);
        }
      });
    });
  }, []);

  // Depr Closing = sum of 7 rows above (same column, same table)
  const setupDeprClosingFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const deprClosingInputs = rowData.verticalFields.filter(info => info.verticalType === 'depr-closing');

      if (deprClosingInputs.length === 0) return;

      deprClosingInputs.forEach(closingInfo => {
        const input = closingInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        if (currentRowIndex < 7) return;

        const formulaParts: string[] = [];

        // Sum 7 rows above (same column)
        for (let i = 1; i <= 7; i++) {
          const prevRow = rows[currentRowIndex - i] as HTMLTableRowElement;
          if (!prevRow.cells || prevRow.cells.length <= column) continue;

          const prevCell = prevRow.cells[column];
          const prevInput = prevCell?.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;

          if (!prevInput) continue;

          const prevRowIndex = rowIndicesRef.current.get(prevRow);
          if (prevRowIndex === undefined) continue;

          const prevRowData = inputsByRowRef.current.get(prevRowIndex);
          if (!prevRowData) continue;

          const prevInputInfo = prevRowData.inputs.find(info => info.element === prevInput);
          if (!prevInputInfo) continue;

          const cellRef = hfEngine.simpleCellAddressToString(prevInputInfo.cellAddress, sheetRef.current);
          if (cellRef) {
            formulaParts.push(cellRef);
          }
        }

        if (formulaParts.length > 0) {
          const formula = '=' + formulaParts.join('+');
          hfEngine.setCellContents(closingInfo.cellAddress, formula);
          devLog(`[PPE HF] Depr closing ${closingInfo.memberName}: sum of ${formulaParts.length} rows above`);
        }
      });
    });
  }, []);

  // NBV Opening = Cost Opening - Depr Opening (same column, same table)
  // Navigate UP from NBV Opening row to find Cost Opening and Depr Opening in same column
  const setupNbvOpeningFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const nbvOpeningInputs = rowData.verticalFields.filter(info => info.verticalType === 'nbv-opening');

      if (nbvOpeningInputs.length === 0) return;

      nbvOpeningInputs.forEach(nbvInfo => {
        const input = nbvInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        // Search upward for cost-opening and depr-opening in same column
        let costOpeningInfo: InputInfo | null = null;
        let deprOpeningInfo: InputInfo | null = null;

        for (let i = currentRowIndex - 1; i >= 0 && (!costOpeningInfo || !deprOpeningInfo); i--) {
          const prevRow = rows[i] as HTMLTableRowElement;
          if (!prevRow.cells || prevRow.cells.length <= column) continue;

          const prevCell = prevRow.cells[column];
          const prevInput = prevCell?.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;

          if (!prevInput) continue;

          // Check if this is cost-opening or depr-opening
          if (!costOpeningInfo && prevInput.dataset.costOpening === 'true') {
            const prevRowIndex = rowIndicesRef.current.get(prevRow);
            if (prevRowIndex !== undefined) {
              const prevRowData = inputsByRowRef.current.get(prevRowIndex);
              if (prevRowData) {
                costOpeningInfo = prevRowData.inputs.find(info => info.element === prevInput) || null;
              }
            }
          }

          if (!deprOpeningInfo && prevInput.dataset.deprOpening === 'true') {
            const prevRowIndex = rowIndicesRef.current.get(prevRow);
            if (prevRowIndex !== undefined) {
              const prevRowData = inputsByRowRef.current.get(prevRowIndex);
              if (prevRowData) {
                deprOpeningInfo = prevRowData.inputs.find(info => info.element === prevInput) || null;
              }
            }
          }
        }

        if (costOpeningInfo && deprOpeningInfo) {
          const costRef = hfEngine.simpleCellAddressToString(costOpeningInfo.cellAddress, sheetRef.current);
          const deprRef = hfEngine.simpleCellAddressToString(deprOpeningInfo.cellAddress, sheetRef.current);

          if (costRef && deprRef) {
            const formula = `=${costRef}-${deprRef}`;
            hfEngine.setCellContents(nbvInfo.cellAddress, formula);
            devLog(`[PPE HF] NBV Opening ${nbvInfo.memberName}: ${costRef} - ${deprRef}`);
          }
        } else {
          devLog(`[PPE HF] NBV Opening ${nbvInfo.memberName}: missing cost or depr opening`);
        }
      });
    });
  }, []);

  // NBV Closing = Cost Closing - Depr Closing (same column, same table)
  // Navigate UP from NBV Closing row to find Cost Closing and Depr Closing in same column
  const setupNbvClosingFormulas = useCallback((hfEngine: HyperFormula, sortedRows: Array<[number, RowData]>) => {
    sortedRows.forEach(([rowIndex, rowData]) => {
      const nbvClosingInputs = rowData.verticalFields.filter(info => info.verticalType === 'nbv-closing');

      if (nbvClosingInputs.length === 0) return;

      nbvClosingInputs.forEach(nbvInfo => {
        const input = nbvInfo.element;
        const cell = input.closest('td');
        if (!cell) return;

        const column = cell.cellIndex;
        const row = cell.closest('tr');
        const table = row?.closest('table');

        if (!table) return;

        const rows = Array.from(table.querySelectorAll('tr'));
        const currentRowIndex = rows.indexOf(row as HTMLTableRowElement);

        // Search upward for cost-closing and depr-closing in same column
        let costClosingInfo: InputInfo | null = null;
        let deprClosingInfo: InputInfo | null = null;

        for (let i = currentRowIndex - 1; i >= 0 && (!costClosingInfo || !deprClosingInfo); i--) {
          const prevRow = rows[i] as HTMLTableRowElement;
          if (!prevRow.cells || prevRow.cells.length <= column) continue;

          const prevCell = prevRow.cells[column];
          const prevInput = prevCell?.querySelector('input[data-equity-member][data-qname]') as HTMLInputElement;

          if (!prevInput) continue;

          // Check if this is cost-closing or depr-closing
          if (!costClosingInfo && prevInput.dataset.costClosing === 'true') {
            const prevRowIndex = rowIndicesRef.current.get(prevRow);
            if (prevRowIndex !== undefined) {
              const prevRowData = inputsByRowRef.current.get(prevRowIndex);
              if (prevRowData) {
                costClosingInfo = prevRowData.inputs.find(info => info.element === prevInput) || null;
              }
            }
          }

          if (!deprClosingInfo && prevInput.dataset.deprClosing === 'true') {
            const prevRowIndex = rowIndicesRef.current.get(prevRow);
            if (prevRowIndex !== undefined) {
              const prevRowData = inputsByRowRef.current.get(prevRowIndex);
              if (prevRowData) {
                deprClosingInfo = prevRowData.inputs.find(info => info.element === prevInput) || null;
              }
            }
          }
        }

        if (costClosingInfo && deprClosingInfo) {
          const costRef = hfEngine.simpleCellAddressToString(costClosingInfo.cellAddress, sheetRef.current);
          const deprRef = hfEngine.simpleCellAddressToString(deprClosingInfo.cellAddress, sheetRef.current);

          if (costRef && deprRef) {
            const formula = `=${costRef}-${deprRef}`;
            hfEngine.setCellContents(nbvInfo.cellAddress, formula);
            devLog(`[PPE HF] NBV Closing ${nbvInfo.memberName}: ${costRef} - ${deprRef}`);
          }
        } else {
          devLog(`[PPE HF] NBV Closing ${nbvInfo.memberName}: missing cost or depr closing`);
        }
      });
    });
  }, []);

  // Refresh horizontal calculations in a row
  const refreshHorizontalCalculationsInRow = useCallback((rowIndex: number) => {
    if (!engine) return;

    const rowData = inputsByRowRef.current.get(rowIndex);
    if (!rowData || !rowData.parentInput) return;

    const parentInfo = rowData.parentInput;
    const value = engine.getCellValue(parentInfo.cellAddress);
    const formattedValue = formatNumber(value);

    if (parentInfo.element.value !== formattedValue) {
      parentInfo.element.value = formattedValue;
      parentInfo.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
        detail: {
          fieldName: parentInfo.element.name,
          contextRef: parentInfo.element.dataset.contextRef,
          value: formattedValue
        }
      }));
    }
  }, [engine, formatNumber]);

  // Refresh vertical calculations
  const refreshVerticalCalculations = useCallback(() => {
    if (!engine) return;

    inputsByRowRef.current.forEach((rowData) => {
      if (rowData.verticalFields.length === 0) return;

      rowData.verticalFields.forEach(info => {
        const value = engine.getCellValue(info.cellAddress);
        const formattedValue = formatNumber(value);

        if (info.element.value !== formattedValue) {
          info.element.value = formattedValue;
          info.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
            detail: {
              fieldName: info.element.name,
              contextRef: info.element.dataset.contextRef,
              value: formattedValue
            }
          }));
        }
      });
    });
  }, [engine, formatNumber]);

  // Refresh all calculations
  const refreshAllCalculations = useCallback(() => {
    if (!engine) return;

    // Refresh horizontal calculations (parent members)
    inputsByRowRef.current.forEach((rowData, rowIndex) => {
      refreshHorizontalCalculationsInRow(rowIndex);
    });

    // Refresh vertical calculations
    refreshVerticalCalculations();
  }, [engine, refreshHorizontalCalculationsInRow, refreshVerticalCalculations]);

  // Handle input change
  const handleInputChange = useCallback((input: HTMLInputElement) => {
    if (updatingCellRef.current || !engine) return;
    updatingCellRef.current = true;

    try {
      const memberName = input.dataset.equityMember;
      const contextRef = input.dataset.contextRef;
      const qname = input.dataset.qname;

      if (!memberName || !contextRef || !qname) return;

      const row = input.closest('tr');
      if (!row) return;

      const rowIndex = rowIndicesRef.current.get(row as HTMLTableRowElement);
      if (rowIndex === undefined) return;

      const rowData = inputsByRowRef.current.get(rowIndex);
      if (!rowData) return;

      const inputInfo = rowData.inputs.find(info =>
        info.element === input ||
        (info.memberName === memberName &&
          info.qname === qname &&
          info.contextRef === contextRef)
      );

      if (!inputInfo) return;

      let value = 0;
      if (input.value && input.value.trim() !== '') {
        if ((window as any).numeral) {
          value = (window as any).numeral(input.value).value() || 0;
        } else {
          value = parseFloat(input.value.replace(/[^\d.-]/g, '')) || 0;
        }
      }

      engine.setCellContents(inputInfo.cellAddress, value);

      // Refresh calculations
      requestAnimationFrame(() => {
        refreshHorizontalCalculationsInRow(rowIndex);
        refreshVerticalCalculations();
      });
    } catch (error) {
      console.error("Error handling PPE input change:", error);
    } finally {
      updatingCellRef.current = false;
    }
  }, [engine, refreshHorizontalCalculationsInRow, refreshVerticalCalculations]);

  // Public API: handleFieldChange
  const handleFieldChangeAPI = useCallback((fieldName: string, contextRef: string, value: string) => {
    if (!engine) return;

    const input = document.querySelector(
      `input[name="${fieldName}"][data-context-ref="${contextRef}"]`
    ) as HTMLInputElement;

    if (input) {
      input.value = value;
      handleInputChange(input);
    }
  }, [engine, handleInputChange]);

  // Public API: getCalculatedValue
  const getCalculatedValueAPI = useCallback((fieldName: string, contextRef: string): number | null => {
    if (!engine || !isReady) return null;

    for (const [rowIndex, rowData] of Array.from(inputsByRowRef.current.entries())) {
      const inputInfo = rowData.inputs.find((info: InputInfo) => {
        const inputFieldName = info.element.name;
        const inputContextRef = info.element.dataset.contextRef;
        return inputFieldName === fieldName && inputContextRef === contextRef;
      });

      if (inputInfo) {
        const value = engine.getCellValue(inputInfo.cellAddress);
        if (typeof value === 'number') return value;
        if (typeof value === 'string') {
          const numValue = parseFloat(value);
          return isNaN(numValue) ? null : numValue;
        }
        return null;
      }
    }

    return null;
  }, [engine, isReady]);

  // Setup event listeners
  useEffect(() => {
    const handleInput = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.socie-table input[data-equity-member][data-qname]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    const handleChange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      if (target.matches('.socie-table input[data-equity-member][data-qname]:not([readonly])')) {
        handleInputChange(target);
      }
    };

    document.addEventListener('input', handleInput);
    document.addEventListener('change', handleChange);

    return () => {
      document.removeEventListener('input', handleInput);
      document.removeEventListener('change', handleChange);
    };
  }, [handleInputChange]);

  // Initialize HyperFormula engine
  useEffect(() => {
    if (engine) return; // Already initialized

    try {
      const hfEngine = HyperFormula.buildEmpty({
        licenseKey: 'gpl-v3',
        precisionRounding: 10,
        useColumnIndex: false
      });

      const sheetName = hfEngine.addSheet('ppe');
      const sheetId = hfEngine.getSheetId(sheetName);
      sheetRef.current = sheetId || 0;

      setEngine(hfEngine);

      // Delay mapping until DOM is ready
      setTimeout(() => {
        const ppeTable = document.querySelector('.socie-table');
        if (!ppeTable) {
          console.warn('[PPE HF] Table not found, skipping initialization');
          setIsReady(false);
          return;
        }

        // Map inputs by row
        mapInputsByRow(hfEngine);

        // Setup horizontal formulas (sum → parent)
        setupHorizontalFormulas(hfEngine);

        // Setup vertical formulas
        setupVerticalFormulas(hfEngine);

        // Initial calculation - populate all readonly fields
        inputsByRowRef.current.forEach((rowData, rowIndex) => {
          // Refresh parent (horizontal sum)
          if (rowData.parentInput) {
            const value = hfEngine.getCellValue(rowData.parentInput.cellAddress);
            const formattedValue = formatNumber(value);
            rowData.parentInput.element.value = formattedValue;

            rowData.parentInput.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
              detail: {
                fieldName: rowData.parentInput.element.name,
                contextRef: rowData.parentInput.element.dataset.contextRef,
                value: formattedValue
              }
            }));
          }

          // Refresh vertical calculations
          rowData.verticalFields.forEach(info => {
            const value = hfEngine.getCellValue(info.cellAddress);
            const formattedValue = formatNumber(value);
            info.element.value = formattedValue;

            info.element.dispatchEvent(new CustomEvent('hyperformulaUpdate', {
              detail: {
                fieldName: info.element.name,
                contextRef: info.element.dataset.contextRef,
                value: formattedValue
              }
            }));
          });
        });

        devLog('[PPE HF] Initialization complete');
        setIsReady(true);
      }, 500);
    } catch (error) {
      console.error("[PPE HF] Error initializing:", error);
      setIsReady(false);
    }
  }, [mapInputsByRow, setupHorizontalFormulas, setupVerticalFormulas, formatNumber]);

  // Listen for formDataLoaded event to re-sync from DOM
  useEffect(() => {
    if (!engine) return;

    const handleFormDataLoaded = () => {
      devLog('[PPE HF] formDataLoaded event - syncing from DOM...');

      setTimeout(() => {
        // Update editable cells from current DOM values (keep formulas intact)
        inputsByRowRef.current.forEach((rowData) => {
          rowData.inputs.forEach((info) => {
            // Skip readonly cells - they contain formulas
            if (info.element.readOnly) return;

            let value = 0;
            const raw = info.element.value;
            if (raw && raw.trim() !== '') {
              if ((window as any).numeral) {
                value = (window as any).numeral(raw).value() || 0;
              } else {
                value = parseFloat(raw.replace(/[^\d.-]/g, '')) || 0;
              }
            }

            engine.setCellContents(info.cellAddress, value);
          });
        });

        // Refresh all calculated fields
        refreshAllCalculations();

        devLog('[PPE HF] Sync complete');

        // Signal completion after settling
        let rafCount = 0;
        const waitForSettled = () => {
          rafCount++;
          if (rafCount >= 3) {
            setTimeout(() => {
              document.dispatchEvent(new CustomEvent('ppeCalculationsComplete'));
            }, 200);
          } else {
            requestAnimationFrame(waitForSettled);
          }
        };
        requestAnimationFrame(waitForSettled);
      }, 100);
    };

    document.addEventListener('formDataLoaded', handleFormDataLoaded);

    return () => {
      document.removeEventListener('formDataLoaded', handleFormDataLoaded);
    };
  }, [engine, refreshAllCalculations]);

  return {
    engine,
    isReady,
    readOnlyFields: readOnlyFieldsRef.current,
    handleFieldChange: handleFieldChangeAPI,
    getCalculatedValue: getCalculatedValueAPI,
    refreshAllCalculations,
  };
};
